import type { Call, Device } from '@twilio/voice-sdk';
import { fetchVoiceToken } from './api';

/**
 * The softphone seam (E2E.md „Test doubles"): the SPA talks to one `BrimoVoiceClient` per tab. In the
 * browser that is the Twilio Voice SDK (`Device` with the token of `POST /api/telephony/token`); an E2E run
 * replaces it by defining `window.__brimoVoice` before the app loads. The client never dials a number: the
 * call row is created by `POST /api/calls` and the device connects with `params: { callId }` only. Dialer users also
 * `listen()` for inbound calls: the device registers with Twilio and each ringing call arrives with the custom
 * parameters of the server's `<Client>` (call id, contact, name, number — docs/dev/api-dialer.md „Inbound calls").
 */

export type VoiceCallEvent = 'ringing' | 'connected' | 'ended' | 'failed';

export interface BrimoVoiceCall {
  readonly callId: string;
  on(event: VoiceCallEvent, handler: (detail: { at: string; reason?: string }) => void): () => void;
  mute(muted: boolean): void;
  isMuted(): boolean;
  hangup(): void;
  localStream(): MediaStream | null;
  remoteStream(): MediaStream | null;
}

/** A ringing inbound call: accept it or decline it; `canceled` = taken by another setter, caller hung up, timeout. */
export interface BrimoIncomingCall {
  readonly callId: string;
  readonly contactId: string;
  /** Null for a caller the server created for this call: show the number. */
  readonly contactName: string | null;
  readonly from: string;
  accept(): BrimoVoiceCall;
  reject(): void;
  onCanceled(handler: () => void): () => void;
}

export interface BrimoVoiceClient {
  readonly kind: 'twilio' | 'test';
  /** Twilio: token + Device; rejects with the ApiError of /telephony/token. */
  ready(): Promise<void>;
  connect(input: { callId: string }): Promise<BrimoVoiceCall>;
  /**
   * Registers the device for inbound calls; resolves false when the token does not allow them. A client without
   * the method (an older test double) never rings.
   */
  listen?(onIncoming: (call: BrimoIncomingCall) => void): Promise<boolean>;
  destroy(): void;
}

/** The browser refused the microphone (getUserMedia / Twilio 31401, 31402, 31208). */
export class MicrophoneError extends Error {
  constructor() {
    super('Kein Zugriff auf das Mikrofon. Bitte erlauben Sie den Zugriff im Browser.');
    this.name = 'MicrophoneError';
  }
}

const MICROPHONE_ERROR_CODES = new Set([31208, 31401, 31402]);
/** `Device.State.Registered` without importing the SDK eagerly (it loads on the first `ready()`). */
const TWILIO_REGISTERED = 'registered';

export function isMicrophoneFailure(error: unknown): boolean {
  if (error instanceof MicrophoneError) return true;
  if (typeof error !== 'object' || error === null) return false;
  const code = 'code' in error ? error.code : null;
  const name = 'name' in error ? error.name : null;
  return (
    (typeof code === 'number' && MICROPHONE_ERROR_CODES.has(code)) ||
    name === 'NotAllowedError' ||
    name === 'PermissionDeniedError' ||
    name === 'NotFoundError'
  );
}

function now(): string {
  return new Date().toISOString();
}

type Handler = (detail: { at: string; reason?: string }) => void;

/** One Twilio `Call` behind the seam; the SDK's events are mapped onto the four call events. */
class TwilioVoiceCall implements BrimoVoiceCall {
  private readonly handlers = new Map<VoiceCallEvent, Set<Handler>>();
  private finished = false;

  constructor(
    readonly callId: string,
    private readonly call: Call,
  ) {
    call.on('ringing', () => this.emit('ringing'));
    call.on('accept', () => this.emit('connected'));
    call.on('disconnect', () => this.finish('ended'));
    call.on('cancel', () => this.finish('ended', 'canceled'));
    call.on('reject', () => this.finish('ended', 'rejected'));
    call.on('error', (error: { code?: number; message?: string }) => {
      this.finish('failed', error.code ? `twilio ${error.code}` : (error.message ?? 'error'));
    });
  }

  private emit(event: VoiceCallEvent, reason?: string): void {
    const detail: { at: string; reason?: string } = { at: now() };
    if (reason) detail.reason = reason;
    for (const handler of this.handlers.get(event) ?? []) handler(detail);
  }

  private finish(event: 'ended' | 'failed', reason?: string): void {
    if (this.finished) return;
    this.finished = true;
    this.emit(event, reason);
  }

  on(event: VoiceCallEvent, handler: Handler): () => void {
    const set = this.handlers.get(event) ?? new Set<Handler>();
    set.add(handler);
    this.handlers.set(event, set);
    return () => set.delete(handler);
  }

  mute(muted: boolean): void {
    this.call.mute(muted);
  }

  isMuted(): boolean {
    return this.call.isMuted();
  }

  hangup(): void {
    this.call.disconnect();
  }

  localStream(): MediaStream | null {
    return this.call.getLocalStream() ?? null;
  }

  remoteStream(): MediaStream | null {
    return this.call.getRemoteStream() ?? null;
  }
}

/** An inbound Twilio `Call` before it is accepted; its custom parameters come from the server's `<Client>`. */
class TwilioIncomingCall implements BrimoIncomingCall {
  readonly callId: string;
  readonly contactId: string;
  readonly contactName: string | null;
  readonly from: string;

  constructor(private readonly call: Call) {
    const parameters = call.customParameters;
    this.callId = parameters.get('callId') ?? '';
    this.contactId = parameters.get('contactId') ?? '';
    this.contactName = parameters.get('contactName') ?? null;
    this.from = parameters.get('from') ?? call.parameters['From'] ?? '';
  }

  accept(): BrimoVoiceCall {
    const wrapped = new TwilioVoiceCall(this.callId, this.call);
    this.call.accept();
    return wrapped;
  }

  reject(): void {
    this.call.reject();
  }

  onCanceled(handler: () => void): () => void {
    this.call.on('cancel', handler);
    return () => this.call.off('cancel', handler);
  }
}

/** The Twilio implementation: one `Device` per tab, token in memory only, refreshed on `tokenWillExpire`. */
class TwilioVoiceClient implements BrimoVoiceClient {
  readonly kind = 'twilio' as const;
  private device: Device | null = null;
  private pending: Promise<void> | null = null;
  private expired = false;
  private incomingAllowed = false;
  private onIncoming: ((call: BrimoIncomingCall) => void) | null = null;

  ready(): Promise<void> {
    if (this.device !== null && !this.expired) return Promise.resolve();
    if (this.expired) this.teardownDevice();
    this.pending ??= this.init().catch((error: unknown) => {
      this.pending = null;
      throw error;
    });
    return this.pending;
  }

  private async init(): Promise<void> {
    const token = await fetchVoiceToken();
    const { Device: TwilioDevice } = await import('@twilio/voice-sdk');
    const device = new TwilioDevice(token.token, {
      edge: token.edge,
      closeProtection: false,
      logLevel: 'error',
    });
    device.on('incoming', (call: Call) => {
      if (this.onIncoming === null) call.reject();
      else this.onIncoming(new TwilioIncomingCall(call));
    });
    device.on('tokenWillExpire', () => {
      fetchVoiceToken().then(
        (next) => device.updateToken(next.token),
        () => {
          // The next `ready()` builds a fresh device with a new token.
          this.expired = true;
        },
      );
    });
    this.device = device;
    this.expired = false;
    this.incomingAllowed = token.incoming;
    // A device rebuilt after an expired token registers again for the listener of the previous one.
    if (this.onIncoming !== null && token.incoming) await device.register();
  }

  async listen(onIncoming: (call: BrimoIncomingCall) => void): Promise<boolean> {
    await this.ready();
    const device = this.device;
    if (device === null || !this.incomingAllowed) return false;
    this.onIncoming = onIncoming;
    if ((device.state as string) !== TWILIO_REGISTERED) await device.register();
    return true;
  }

  async connect(input: { callId: string }): Promise<BrimoVoiceCall> {
    await this.ready();
    const device = this.device;
    if (device === null) throw new Error('voice device missing');
    try {
      const call = await device.connect({ params: { callId: input.callId } });
      return new TwilioVoiceCall(input.callId, call);
    } catch (error) {
      if (isMicrophoneFailure(error)) throw new MicrophoneError();
      throw error;
    }
  }

  private teardownDevice(): void {
    this.device?.destroy();
    this.device = null;
    this.pending = null;
  }

  destroy(): void {
    this.onIncoming = null;
    this.teardownDevice();
    this.expired = false;
  }
}

let shared: BrimoVoiceClient | null = null;

/** The tab's voice client: the E2E double when `window.__brimoVoice` exists, else Twilio. */
export function getVoiceClient(): BrimoVoiceClient {
  if (shared !== null) return shared;
  const factory = typeof window !== 'undefined' ? window.__brimoVoice : undefined;
  shared = typeof factory === 'function' ? factory() : new TwilioVoiceClient();
  return shared;
}

/** Destroys the device (sign-out, another user in the tab). */
export function destroyVoiceClient(): void {
  shared?.destroy();
  shared = null;
}
